import { LuckyTurnipsActions } from './LuckyTurnipsActions';
import { takeEvery, select, put } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { Immutable } from '../../Utility/Types';
import { ILuckyTurnipsState } from './ILuckyTurnipsState';
import { report } from '../../report';
import { ILuckyTurnipLoot, luckyTurnipsLoot, STAR_CHEST_TIER_DATA } from '../../Constants/Chests';
import { Quests } from '../Player/QuestType';
import { TActionType, TransactionType } from '../../Utility/Tracking/DataSet/Transaction';
import { PlayerActions } from '../Player/PlayerActions';
import { getCurrentLevelNumber } from '../Player/PlayerLevelHelper';
import { PlayerReducer } from '../Player/PlayerReducer';
import { resetReward, trackReward } from '../../Utility/Tracking/TrackingHelpers/Resource';
import { trackResourceEvent } from '../../Utility/Tracking/TrackingHelpers/Transaction';
import { IRewardData } from '../../Utility/RewardHelper';
import LevelHelper from '../../Utility/LevelHelper';
import { IGameState } from '../Game/IGameState';
import { GameActions } from '../Game/GameActions';
import { QuestStateHelper } from '../Player/Quests/QuestStateHelper';
import { StarChestStateHelper } from '../Player/StarChests/StarChestStateHelper';

export function* LuckyTurnipsSaga() {
    yield takeEvery(getType(LuckyTurnipsActions.handleTurnipClick), handleTurnipClick);
    yield takeEvery(getType(LuckyTurnipsActions.handleClaimClick), handleClaimClick);
}

function* handleTurnipClick(action: ReturnType<typeof LuckyTurnipsActions.handleTurnipClick>) {
    const state: IGameState = yield select();
    const turnipIndex = action.payload;

    if (!canPickTurnip(state.playerState.luckyTurnipsState, turnipIndex)) return;

    const playerState = QuestStateHelper.incrementQuestProgressBy(
        state.playerState,
        Quests.PICK_TURNIPS_STAR_CHEST,
        1,
        TActionType.LUCKY_TURNIPS_CLAIM,
    );

    yield put(PlayerActions.setPlayerState({
        ...playerState,
        luckyTurnipsState:
            pickTurnip(playerState.luckyTurnipsState, turnipIndex) ??
            playerState.luckyTurnipsState,
    }));
}

function* handleClaimClick() {
    let state: IGameState = yield select();

    if (!canClaimRewards(state.playerState.luckyTurnipsState)) return;

    const highestUnlockedLevel = getCurrentLevelNumber(state.playerState);
    const rewards = state.playerState.luckyTurnipsState.pickedLootIndices.map(
        (lootIndex) => luckyTurnipLootToRewardData(luckyTurnipsLoot[lootIndex], highestUnlockedLevel),
    );

    state = PlayerReducer(
        state,
        PlayerActions.receiveRewards({
            rewards,
            // FIXME: refactor this away, `receiveRewards` should be capable
            //        of properly tracking this
            dontTrack: true,
            // Display the rewards when back to world map
            queueTask: true,
            groupTasks: true,
        }),
    );

    // Remove the star chest level so that the next one can spawn
    state = { ...state, playerState: StarChestStateHelper.setStarChestLevel(state.playerState, null) };

    for (const reward of rewards) {
        trackReward(reward);
    }

    trackResourceEvent(state, {
        transactionType: TransactionType.LUCKY_TURNIPS_CLAIM,
        tActionType: TActionType.LUCKY_TURNIPS_CLAIM,
    });

    for (const reward of rewards) {
        resetReward(reward.type);
    }

    yield put(GameActions.setState({
        ...state,
        playerState: {
            ...state.playerState,
            // Finish the minigame and prevent further `claimRewards`
            luckyTurnipsState: null,
        },
    }));
}

function canPickTurnip(luckyTurnipsState: Immutable<ILuckyTurnipsState> | null, turnipIndex: number): boolean {
    if (!luckyTurnipsState || getPicksLeft(luckyTurnipsState) === 0) {
        return false;
    }

    const lootIndex = luckyTurnipsState.lootOrder[turnipIndex];

    if (lootIndex === undefined) {
        report('Failed to find loot index from pick', {
            once: true,
            tags: {
                turnipIndex,
                lootOrder: luckyTurnipsState.lootOrder.join(','),
            },
        });

        return false;
    }

    return !luckyTurnipsState.pickedLootIndices.includes(lootIndex);
}

function getPicksLeft(luckyTurnipsState: Immutable<ILuckyTurnipsState>) {
    const picks = STAR_CHEST_TIER_DATA[luckyTurnipsState.starChestTier].luckyTurnipPicks.length;
    return Math.max(picks - luckyTurnipsState.pickedLootIndices.length, 0);
}

function pickTurnip(
    luckyTurnipsState: ILuckyTurnipsState | null,
    turnipIndex: number,
): ILuckyTurnipsState | undefined {
    if (!canPickTurnip(luckyTurnipsState, turnipIndex)) return;

    return {
        ...luckyTurnipsState,
        // Add picked `lootIndex` to the `pickedLootIndices`
        pickedLootIndices: [
            ...luckyTurnipsState.pickedLootIndices,
            luckyTurnipsState.lootOrder[turnipIndex],
        ],
    };
}

function canClaimRewards(luckyTurnipsState: Immutable<ILuckyTurnipsState> | null): boolean {
    return Boolean(luckyTurnipsState && getPicksLeft(luckyTurnipsState) <= 0);
}

export function luckyTurnipLootToRewardData(
    loot: Immutable<ILuckyTurnipLoot>,
    highestUnlockedLevel: number,
): IRewardData {
    let amount = loot.amount;

    if (loot.levelCostFactor !== undefined) {
        const levelCost = LevelHelper.getLevelCost(highestUnlockedLevel);
        amount = Math.round(amount * levelCost * loot.levelCostFactor);
    }

    return { value: amount, type: loot.rewardType };
}
